import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MapPin, Clock, Users, Flame, Sparkles, Zap, Filter, X, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import ImageCarousel from '../components/ImageCarousel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Trips = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // Filter states from URL
  const selectedRegion = searchParams.get('region') || '';
  const selectedVibe = searchParams.get('vibe') || '';
  const selectedBurnLevel = searchParams.get('burn_level') || '';

  const regions = [
    { key: 'north_central_america', name: 'North & Central America', name_es: 'Norte y Centroamérica' },
    { key: 'south_america', name: 'South America', name_es: 'Sudamérica' },
    { key: 'caribbean', name: 'Caribbean', name_es: 'Caribe' },
  ];

  const vibes = [
    { key: 'adventure', name: 'Adventure', name_es: 'Aventura', emoji: '🏔️' },
    { key: 'culture', name: 'Culture', name_es: 'Cultura', emoji: '🎭' },
    { key: 'relaxation', name: 'Relaxation', name_es: 'Relajación', emoji: '🌴' },
  ];

  const burnLevels = [
    { key: '1', name: 'The Socialite', name_es: 'El Socialité', icon: Sparkles, color: 'purple' },
    { key: '2', name: 'The Vibe', name_es: 'La Vibra', icon: Flame, color: 'orange' },
    { key: '3', name: 'The Badass', name_es: 'El Temerario', icon: Zap, color: 'red' },
  ];

  const burnIcons = { 1: Sparkles, 2: Flame, 3: Zap };
  const burnColors = {
    1: 'from-purple-500 to-pink-500',
    2: 'from-orange-500 to-amber-500',
    3: 'from-red-500 to-orange-600'
  };
  const burnBgColors = {
    1: 'bg-purple-600 text-white',
    2: 'bg-orange-500 text-white',
    3: 'bg-red-600 text-white'
  };

  useEffect(() => {
    const fetchTrips = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (selectedRegion) params.append('region', selectedRegion);
        if (selectedVibe) params.append('vibe', selectedVibe);
        if (selectedBurnLevel) params.append('burn_level', selectedBurnLevel);
        params.append('lang', language);

        const response = await fetch(`${API_URL}/api/trips?${params.toString()}`);
        const data = await response.json();
        setTrips(data.trips || []);
      } catch (error) {
        console.error('Error fetching trips:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTrips();
  }, [selectedRegion, selectedVibe, selectedBurnLevel, language]);

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  const hasActiveFilters = selectedRegion || selectedVibe || selectedBurnLevel;

  return (
    <div className="min-h-screen bg-gray-50 pt-20" data-testid="trips-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4">
            {language === 'es' ? 'Encuentra Tu Aventura' : 'Find Your Adventure'}
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            {language === 'es' 
              ? 'Explora todos nuestros viajes y filtra por región, vibra o nivel de intensidad' 
              : 'Explore all our trips and filter by region, vibe, or intensity level'}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Filter Bar */}
        <div className="bg-white rounded-2xl shadow-lg p-4 mb-8" data-testid="filter-bar">
          <div className="flex flex-wrap items-center gap-4">
            {/* Region Filter */}
            <div className="relative">
              <select
                data-testid="region-filter"
                value={selectedRegion}
                onChange={(e) => updateFilter('region', e.target.value)}
                className="appearance-none bg-gray-100 border-0 rounded-lg px-4 py-2.5 pr-10 font-medium text-gray-700 focus:ring-2 focus:ring-[#84bc00] cursor-pointer"
              >
                <option value="">{language === 'es' ? 'Todas las Regiones' : 'All Regions'}</option>
                {regions.map((r) => (
                  <option key={r.key} value={r.key}>
                    {language === 'es' ? r.name_es : r.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
            </div>

            {/* Vibe Filter */}
            <div className="relative">
              <select
                data-testid="vibe-filter"
                value={selectedVibe}
                onChange={(e) => updateFilter('vibe', e.target.value)}
                className="appearance-none bg-gray-100 border-0 rounded-lg px-4 py-2.5 pr-10 font-medium text-gray-700 focus:ring-2 focus:ring-[#84bc00] cursor-pointer"
              >
                <option value="">{language === 'es' ? 'Todas las Vibras' : 'All Vibes'}</option>
                {vibes.map((v) => (
                  <option key={v.key} value={v.key}>
                    {v.emoji} {language === 'es' ? v.name_es : v.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
            </div>

            {/* Burn Level Filter */}
            <div className="relative">
              <select
                data-testid="burn-level-filter"
                value={selectedBurnLevel}
                onChange={(e) => updateFilter('burn_level', e.target.value)}
                className="appearance-none bg-gray-100 border-0 rounded-lg px-4 py-2.5 pr-10 font-medium text-gray-700 focus:ring-2 focus:ring-[#84bc00] cursor-pointer"
              >
                <option value="">{language === 'es' ? 'Todos los Niveles' : 'All Levels'}</option>
                {burnLevels.map((b) => (
                  <option key={b.key} value={b.key}>
                    Level {b.key}: {language === 'es' ? b.name_es : b.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button
                onClick={clearFilters}
                variant="ghost"
                className="text-gray-500 hover:text-gray-700"
                data-testid="clear-filters-btn"
              >
                <X className="w-4 h-4 mr-1" />
                {language === 'es' ? 'Limpiar' : 'Clear'}
              </Button>
            )}

            {/* Results Count */}
            <div className="ml-auto text-gray-500 text-sm">
              {trips.length} {trips.length === 1 
                ? (language === 'es' ? 'viaje encontrado' : 'trip found') 
                : (language === 'es' ? 'viajes encontrados' : 'trips found')}
            </div>
          </div>
        </div>

        {/* Active Filter Tags */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 mb-6">
            {selectedRegion && (
              <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#84bc00]/10 text-[#84bc00] rounded-full text-sm font-medium">
                {regions.find(r => r.key === selectedRegion)?.[language === 'es' ? 'name_es' : 'name']}
                <button onClick={() => updateFilter('region', '')} className="ml-1 hover:text-[#6a9600]">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {selectedVibe && (
              <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#ffb84d]/10 text-[#ffb84d] rounded-full text-sm font-medium">
                {vibes.find(v => v.key === selectedVibe)?.[language === 'es' ? 'name_es' : 'name']}
                <button onClick={() => updateFilter('vibe', '')} className="ml-1 hover:text-amber-600">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {selectedBurnLevel && (
              <span className={`inline-flex items-center gap-1 px-3 py-1.5 ${burnBgColors[selectedBurnLevel]} rounded-full text-sm font-medium`}>
                Level {selectedBurnLevel}
                <button onClick={() => updateFilter('burn_level', '')} className="ml-1 hover:opacity-70">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="animate-pulse bg-gray-200 rounded-2xl h-96"></div>
            ))}
          </div>
        ) : trips.length === 0 ? (
          /* Empty State */
          <div className="text-center py-20">
            <div className="text-6xl mb-4">🏝️</div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              {language === 'es' ? 'No hay viajes con estos filtros' : 'No trips match these filters'}
            </h3>
            <p className="text-gray-500 mb-6">
              {language === 'es' 
                ? 'Intenta ajustar tus filtros o explorar otras opciones' 
                : 'Try adjusting your filters or explore other options'}
            </p>
            <Button onClick={clearFilters} className="bg-[#84bc00] hover:bg-[#6a9600] text-black font-bold">
              {language === 'es' ? 'Ver Todos los Viajes' : 'View All Trips'}
            </Button>
          </div>
        ) : (
          /* Trips Grid */
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {trips.map((trip) => {
              const BurnIcon = burnIcons[trip.burn_level] || Flame;
              const burnGradient = burnColors[trip.burn_level] || burnColors[2];
              const burnBg = burnBgColors[trip.burn_level] || burnBgColors[2];

              return (
                <div
                  key={trip.id}
                  data-testid={`trip-card-${trip.id}`}
                  className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                >
                  {/* Image Carousel */}
                  <div className="relative h-56 overflow-hidden">
                    <ImageCarousel
                      mainImage={trip.image}
                      images={trip.images || []}
                      alt={trip.name}
                      className="h-full"
                      aspectRatio="video"
                      showDots={true}
                      showArrows={true}
                    />
                    {/* Burn Level Badge */}
                    <div className={`absolute top-4 left-4 px-3 py-1.5 rounded-full ${burnBg} backdrop-blur-sm flex items-center gap-1.5 z-20`}>
                      <BurnIcon className="w-4 h-4" />
                      <span className="text-xs font-bold">{trip.burn_label}</span>
                    </div>
                    {/* Price */}
                    <div className="absolute bottom-4 right-4 bg-black/80 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg z-20">
                      <span className="text-xs text-gray-300">{language === 'es' ? 'desde' : 'from'}</span>
                      <span className="text-lg font-black ml-1">${trip.price?.toLocaleString()}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    {/* Location & Duration */}
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        <span>{trip.location}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>{trip.duration}</span>
                      </div>
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[#84bc00] transition-colors">
                      {trip.name}
                    </h3>

                    {/* Description */}
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {trip.description}
                    </p>

                    {/* Vibes Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {trip.vibes?.map((vibe, i) => (
                        <span 
                          key={i}
                          className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full capitalize"
                        >
                          {vibe}
                        </span>
                      ))}
                    </div>

                    {/* CTA */}
                    <Button
                      onClick={() => navigate(`/trips/${trip.id}`)}
                      className={`w-full bg-gradient-to-r ${burnGradient} hover:opacity-90 text-white font-bold py-3 rounded-lg transition-all`}
                    >
                      {language === 'es' ? 'Ver Detalles' : 'View Details'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Trips;
